import { Add, Delete, DescriptionOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { CollapsibleCard, DeleteModal } from "../v3";
import { get_date } from "../generalComponets";
import { VIEW_OFFICE_DOCUMENTS } from "../../constants";
import { OfficeService } from "../../services/office.service";
import { FileService } from "../../services/file.service";
import { useHasPermission } from "../../utils";
import { AddOfficeDocumentModal } from "./AddOfficeDocumentModal.component";

export const OfficeDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [docDeleteModal, setDocDeleteModal] = useState({
    isOpen: false,
    docId: null,
  });

  const hasManageDocuments = useHasPermission(VIEW_OFFICE_DOCUMENTS);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await OfficeService.listDocuments({ limit: 20, page: 1 });
      setDocuments(Array.isArray(data?.rows) ? data.rows : []);
      setDocCount(data?.count || 0);
    } catch (error) {
      console.error("Failed to fetch office documents:", error);
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleViewDoc = async (doc) => {
    try {
      const file = await FileService.getFileDetails(doc.id);

      if (file?.url) {
        window.open(file.url, "_blank");
        return;
      }

      toast.error("Failed to fetch document link");
    } catch (error) {
      console.error("Office document view error:", error);
      toast.error("Error retrieving document");
    }
  };

  const openDocDeleteModal = (docId) => {
    setDocDeleteModal({
      isOpen: true,
      docId,
    });
  };

  const closeDocDeleteModal = () => {
    setDocDeleteModal({
      isOpen: false,
      docId: null,
    });
  };

  const handleDeleteDoc = async () => {
    try {
      await OfficeService.deleteDocument(docDeleteModal.docId);
      toast.success("Document deleted successfully");
      fetchDocuments();
      closeDocDeleteModal();
    } catch (error) {
      console.error("Office document delete error:", error);
      toast.error("Failed to delete document");
    }
  };

  return (
    <>
      <CollapsibleCard
        loading={loading}
        title={`Documents (${docCount})`}
        icon={<DescriptionOutlined />}
        headerButtons={[
          hasManageDocuments && {
            icon: <Add />,
            buttonText: "Add Document",
            tooltip: "Add Document",
            onClick: () => setOpenDocModal(true),
          },
        ].filter(Boolean)}
        itemButtons={[
          {
            icon: <VisibilityOutlined />,
            tooltip: "View",
            onClick: (item) => handleViewDoc(item),
          },
          hasManageDocuments && {
            icon: <Delete />,
            tooltip: "Delete",
            hoverStyles: { color: "#FF1744" },
            onClick: (item) => openDocDeleteModal(item.id),
          },
        ].filter(Boolean)}
        items={
          Array.isArray(documents)
            ? documents.map((doc) => ({
                ...doc,
                date: get_date(doc.createdAt, "DD MMM YYYY"),
                name: doc.originalName,
              }))
            : []
        }
        emptyState={{ title: "No Documents found", subtitle: "Add Documents to get started" }}
      />

      <AddOfficeDocumentModal
        open={openDocModal}
        onClose={() => setOpenDocModal(false)}
        onSuccess={fetchDocuments}
        currentCount={docCount}
      />

      <DeleteModal
        isOpen={docDeleteModal.isOpen}
        onClose={closeDocDeleteModal}
        onConfirm={handleDeleteDoc}
        message="Are you sure you want to delete this document?"
      />
    </>
  );
};
